import { useCallback, useMemo, useRef, useState } from "react";
import { usePostHog } from "posthog-js/react";
import {
  archiveConversation as archiveConversationApi,
  fetchAgentAvailable,
  getTurns,
  listConversations,
  putTurnFeedback,
  unarchiveConversation as unarchiveConversationApi,
  type ConversationSummary,
  type TurnList,
} from "@/services/agentApi";
import {
  createAgentChatSession,
  type AgentChatSession,
  type ClientTool,
  type HistoryMessage,
} from "@/services/agentChat";
import type { ScreenContext } from "../context/screenContext";
import { getScreenTools } from "../context/screenTools";
import { createPostHogLogger } from "@/shared/logging/posthogLogger";
import { setVisibleConversation } from "./speechPlayback";

export type FeedbackRating = "up" | "down";

export interface ChatToolChip {
  name: string;
  done: boolean;
}

export interface ChatEntry {
  id: string;
  role: "user" | "assistant";
  text: string;
  streaming: boolean;
  tools: ChatToolChip[];
  error: string | null;
  /** Persisted turn sequence; set once known, enables feedback. */
  seq: number | null;
  feedbackRating: FeedbackRating | null;
  /** When the turn was created; used for message timestamps in the transcript. */
  createdAtUtc: string | null;
  /** Interrupted by a newer send; excluded from voice playback and feedback. */
  cancelled?: boolean;
}

export type AgentAvailability = "unknown" | "available" | "unavailable";

/** Archived history plus the actions that move a conversation in or out of it. */
export interface ConversationArchive {
  conversations: readonly ConversationSummary[];
  /** Server-side count; `conversations` holds only the most recent page. */
  total: number;
  archive(conversationId: string): Promise<void>;
  unarchive(conversationId: string): Promise<void>;
}

/**
 * The tools offered on a single run: the always-present screen-context reader
 * plus the screen-registered UI tools (#675) that the current screen supports,
 * de-duplicated by name so a screen cannot shadow the built-in reader.
 */
function buildClientTools(
  getContext: () => ScreenContext,
  screenTools: readonly ClientTool[],
): ClientTool[] {
  const clientTools: ClientTool[] = [
    {
      name: "get_screen_context",
      description:
        "Read the user's current screen context (route, active filters, selection, result counts). " +
        "Call it when you need fresh screen state mid-conversation.",
      parameters: { type: "object", properties: {}, additionalProperties: false },
      execute: () => getContext(),
    },
  ];
  for (const tool of screenTools) {
    if (!clientTools.some((t) => t.name === tool.name)) {
      clientTools.push(tool);
    }
  }
  return clientTools;
}

/**
 * Conversation state for the agent drawer (#673): streaming runs, transcript
 * entries with tool activity, history resume, and per-answer feedback. One AG-UI
 * session lives per conversation; resuming an old conversation rebuilds it from
 * the persisted turns because the server is stateless per run.
 */
export function useAgentChat() {
  const posthog = usePostHog();
  const [availability, setAvailability] = useState<AgentAvailability>("unknown");
  const [entries, setEntries] = useState<ChatEntry[]>([]);
  const [streaming, setStreaming] = useState(false);
  const [conversations, setConversations] = useState<ConversationSummary[]>([]);
  const [archivedConversations, setArchivedConversations] = useState<ConversationSummary[]>([]);
  const [archivedTotal, setArchivedTotal] = useState(0);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [conversationsError, setConversationsError] = useState(false);
  // Bumps only when the active conversation is swapped out (new chat or loading
  // another), never on the first send of the current one. A live voice dialog
  // watches this to end itself, since the turn it was mid-way through is gone.
  const [conversationEpoch, setConversationEpoch] = useState(0);

  const sessionRef = useRef<AgentChatSession | null>(null);
  const turnCountRef = useRef(0);
  const conversationIdRef = useRef<string | null>(null);
  // Bumps on every open and on "new chat", so a slow response cannot overwrite a newer choice.
  const loadGenerationRef = useRef(0);
  /** Bumps on every send so an aborted run cannot clear a newer turn's streaming flag. */
  const sendGenerationRef = useRef(0);
  /** Mirrors `streaming` so an interrupt in the same tick is not a no-op. */
  const streamingRef = useRef(false);

  const probe = useCallback(async () => {
    setAvailability((await fetchAgentAvailable()) ? "available" : "unavailable");
  }, []);

  const refreshConversations = useCallback(async () => {
    try {
      const [active, archived] = await Promise.all([
        listConversations(1, 20),
        listConversations(1, 20, true),
      ]);
      setConversations(active.items);
      setArchivedConversations(archived.items);
      setArchivedTotal(archived.total);
      setConversationsError(false);
    } catch {
      // Keep the last list. An empty list plus this flag is a failure, not "no chats yet".
      setConversationsError(true);
    }
  }, []);

  const newConversation = useCallback(() => {
    loadGenerationRef.current += 1;
    sendGenerationRef.current += 1;
    setConversationEpoch((n) => n + 1);
    streamingRef.current = false;
    setStreaming(false);
    setVisibleConversation(null);
    sessionRef.current?.abort();
    sessionRef.current = null;
    conversationIdRef.current = null;
    turnCountRef.current = 0;
    setConversationId(null);
    setEntries([]);
    setLoadError(null);
  }, []);

  const archiveConversation = useCallback(
    async (id: string) => {
      await archiveConversationApi(id);
      posthog.capture("agent_conversation_archived");
      if (conversationIdRef.current === id) {
        newConversation();
      }
      await refreshConversations();
    },
    [newConversation, refreshConversations, posthog],
  );

  const unarchiveConversation = useCallback(
    async (id: string) => {
      await unarchiveConversationApi(id);
      posthog.capture("agent_conversation_unarchived");
      await refreshConversations();
    },
    [refreshConversations, posthog],
  );

  const archive = useMemo<ConversationArchive>(
    () => ({
      conversations: archivedConversations,
      total: archivedTotal,
      archive: archiveConversation,
      unarchive: unarchiveConversation,
    }),
    [archivedConversations, archivedTotal, archiveConversation, unarchiveConversation],
  );

  const loadConversation = useCallback(async (requestedId: string) => {
    const generation = ++loadGenerationRef.current;
    let turns: TurnList;
    try {
      turns = await getTurns(requestedId, 1, 100);
    } catch {
      if (generation !== loadGenerationRef.current) {
        return;
      }
      setLoadError("Couldn't open this conversation.");
      return;
    }
    if (generation !== loadGenerationRef.current) {
      return;
    }

    const loaded: ChatEntry[] = turns.items.map((turn) => ({
      id: `turn-${turn.seq}`,
      role: turn.role === "user" ? "user" : "assistant",
      text: turn.text ?? "",
      streaming: false,
      tools: (turn.toolCalls ?? []).map((t) => ({ name: t.name, done: true })),
      error: turn.error,
      seq: turn.seq,
      feedbackRating:
        turn.feedbackRating === "up" || turn.feedbackRating === "down" ? turn.feedbackRating : null,
      createdAtUtc: turn.createdAtUtc ?? null,
    }));

    const history: HistoryMessage[] = turns.items
      .filter((turn) => (turn.role === "user" || turn.role === "assistant") && turn.text)
      .map((turn) => ({ role: turn.role as "user" | "assistant", content: turn.text ?? "" }));

    setLoadError(null);
    sendGenerationRef.current += 1;
    setConversationEpoch((n) => n + 1);
    streamingRef.current = false;
    setStreaming(false);
    sessionRef.current?.abort();
    setVisibleConversation(requestedId);
    conversationIdRef.current = requestedId;
    turnCountRef.current =
      turns.total > turns.items.length ? Number(turns.total) : turns.items.length;
    sessionRef.current = createAgentChatSession(requestedId, history);
    setConversationId(requestedId);
    setEntries(loaded);
  }, []);

  const dismissLoadError = useCallback(() => {
    setLoadError(null);
  }, []);

  const updateEntry = useCallback((entryId: string, patch: Partial<ChatEntry>) => {
    setEntries((prev) => prev.map((e) => (e.id === entryId ? { ...e, ...patch } : e)));
  }, []);

  const appendToolChip = useCallback((entryId: string, name: string) => {
    setEntries((prev) =>
      prev.map((e) =>
        e.id === entryId ? { ...e, tools: [...e.tools, { name, done: false }] } : e,
      ),
    );
  }, []);

  const completeToolChip = useCallback((entryId: string, name: string) => {
    setEntries((prev) =>
      prev.map((e) =>
        e.id === entryId
          ? { ...e, tools: e.tools.map((t) => (t.name === name ? { ...t, done: true } : t)) }
          : e,
      ),
    );
  }, []);

  const send = useCallback(
    async (
      text: string,
      getContext: () => ScreenContext,
      options?: Readonly<{ interrupt?: boolean }>,
    ) => {
      const trimmed = text.trim();
      if (!trimmed) {
        return;
      }
      if (streamingRef.current && options?.interrupt !== true) {
        return;
      }
      if (streamingRef.current && options?.interrupt === true) {
        // Invalidate the in-flight run before abort so its catch/finally cannot
        // stamp an error on the old entry or clear the new turn's streaming flag.
        sendGenerationRef.current += 1;
        sessionRef.current?.abort();
        // The aborted run still persists a user + assistant pair (the backend
        // PersistAsync runs with CancellationToken.None on disconnect), so
        // advance the counter here. Otherwise the replacement's seq derivation
        // below collides with the interrupted turns and feedback rates the wrong
        // answer.
        turnCountRef.current += 2;
        // Mark the interrupted assistant entry cancelled so voice auto-playback
        // skips its partial answer and selects the replacement instead.
        setEntries((prev) =>
          prev.map((entry) =>
            entry.streaming ? { ...entry, streaming: false, cancelled: true } : entry,
          ),
        );
      }

      // The snapshot rides with the message; the tool lets the agent re-read the
      // screen mid-turn (after it changed something, or later in a long turn).
      const forwardedProps = getContext() as unknown as Record<string, unknown>;
      // Screen-registered UI tools (#675) join per send, so the model is only
      // ever offered the tools the current screen actually supports.
      const clientTools = buildClientTools(getContext, getScreenTools());

      if (sessionRef.current === null) {
        conversationIdRef.current = crypto.randomUUID();
        setVisibleConversation(conversationIdRef.current);
        turnCountRef.current = 0;
        sessionRef.current = createAgentChatSession(conversationIdRef.current, []);
        setConversationId(conversationIdRef.current);
      }

      const userEntry: ChatEntry = {
        id: crypto.randomUUID(),
        role: "user",
        text: trimmed,
        streaming: false,
        tools: [],
        error: null,
        seq: null,
        feedbackRating: null,
        createdAtUtc: new Date().toISOString(),
      };
      const assistantEntry: ChatEntry = {
        id: crypto.randomUUID(),
        role: "assistant",
        text: "",
        streaming: true,
        tools: [],
        error: null,
        seq: null,
        feedbackRating: null,
        createdAtUtc: new Date().toISOString(),
      };
      const generation = ++sendGenerationRef.current;
      streamingRef.current = true;
      setEntries((prev) => [...prev, userEntry, assistantEntry]);
      setStreaming(true);
      posthog.capture("agent_message_sent", { message_length: trimmed.length });

      // A superseded run (interrupted, or left behind by new/load conversation)
      // bumps the generation; its late stream events must not mutate the entry
      // it already abandoned, which would stamp a false error or text on the
      // cancelled reply while the replacement turn streams.
      const isCurrent = () => generation === sendGenerationRef.current;

      try {
        await sessionRef.current.run(
          trimmed,
          forwardedProps,
          {
            onText: (fullText) => {
              if (isCurrent()) {
                updateEntry(assistantEntry.id, { text: fullText });
              }
            },
            onToolStart: (name) => {
              if (isCurrent()) {
                appendToolChip(assistantEntry.id, name);
              }
            },
            onToolEnd: (name) => {
              if (isCurrent()) {
                completeToolChip(assistantEntry.id, name);
              }
            },
            onError: (message) => {
              if (isCurrent()) {
                updateEntry(assistantEntry.id, { error: message });
              }
            },
          },
          clientTools,
        );

        if (generation !== sendGenerationRef.current) {
          return;
        }
        // The backend persists user + assistant turns per run, so the assistant
        // entry's sequence is derivable without a refetch.
        turnCountRef.current += 2;
        updateEntry(assistantEntry.id, { streaming: false, seq: turnCountRef.current });
        updateEntry(userEntry.id, { seq: turnCountRef.current - 1 });
        createPostHogLogger(posthog).agentRunCompleted(clientTools.length);
      } catch (error: unknown) {
        if (generation !== sendGenerationRef.current) {
          return;
        }
        updateEntry(assistantEntry.id, {
          streaming: false,
          error: error instanceof Error ? error.message : "The agent run failed.",
        });
        createPostHogLogger(posthog).agentRunFailed();
      } finally {
        if (generation === sendGenerationRef.current) {
          streamingRef.current = false;
          setStreaming(false);
        }
      }
    },
    [updateEntry, appendToolChip, completeToolChip, posthog],
  );

  const giveFeedback = useCallback(
    async (entryId: string, rating: FeedbackRating | null) => {
      const conversationId = conversationIdRef.current;
      const seq = entries.find((e) => e.id === entryId)?.seq;
      if (!conversationId || seq == null) {
        return;
      }

      await putTurnFeedback(conversationId, seq, rating, null);
      updateEntry(entryId, { feedbackRating: rating });
      if (rating !== null) {
        posthog.capture("agent_feedback_given", { rating });
      }
    },
    [entries, updateEntry, posthog],
  );

  return {
    availability,
    probe,
    entries,
    streaming,
    conversations,
    conversationsError,
    conversationId,
    conversationEpoch,
    loadError,
    dismissLoadError,
    archive,
    refreshConversations,
    newConversation,
    loadConversation,
    send,
    giveFeedback,
  } as const;
}
